import { FC, memo } from 'react';
import classNames from 'classnames';
import { useDragLayer } from 'react-dnd';
import { usePreview } from 'react-dnd-preview';
// Import Types
import type { RowItem } from '../types';
// Import Helpers
import { getRowStyles } from '../helpers';
// Import UI components
import { Icon } from '@geneui/components';
// Import Styles
import styles from '../draggableList.module.scss';

/**
 * Props for PreviewRow component
 */
interface Props {
    formName: string;
    rowContainerClassName?: string;
    rowContentClassName?: string;
    rowRenderer: (props: RowItem, index: number, parentName: string, isDragging: boolean) => React.ReactNode;
}

/**
 * PreviewRow component renders a drag preview for a row in the DnD list.
 * Uses react-dnd and react-dnd-preview to display the row being dragged.
 *
 * @param {Props} props - PreviewRow props
 * @returns {JSX.Element | null} Drag preview row or null if not dragging
 */
const PreviewRow: FC<Props> = ({ formName, rowContainerClassName, rowContentClassName, rowRenderer }) => {
    /** Collects drag preview state from react-dnd-preview */
    const previewCollect = usePreview();

    /** Collects drag state from react-dnd */
    const collect = useDragLayer((monitor) => {
        return {
            item: monitor.getItem(),
            itemType: monitor.getItemType(),
            initialOffset: monitor.getInitialSourceClientOffset(),
            currentOffset: monitor.getSourceClientOffset(),
            isDragging: monitor.isDragging(),
        };
    });

    // Only show preview if dragging and preview is enabled
    if (!previewCollect.display || !collect.isDragging) {
        return null;
    }

    return (
        <div
            className={classNames(
                styles.DND_Row,
                { [styles.draggable]: collect.item.isDraggable, [styles.dragging]: collect.isDragging },
                rowContainerClassName,
            )}
            style={{
                ...getRowStyles({ initialOffset: collect.initialOffset, currentOffset: collect.currentOffset }),
                ...collect.item?.style,
            }}
        >
            <div className={classNames(styles.DND_RowContent, rowContentClassName)}>
                {rowRenderer(collect.item, collect.item.index, formName, collect.isDragging)}
            </div>

            <Icon type="bc-icon-drag" />
        </div>
    );
};

export default memo(PreviewRow);
